"use client"
import { Container, Typography, Box, Paper, useTheme, Link } from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import GavelIcon from "@mui/icons-material/Gavel"
import LaunchIcon from "@mui/icons-material/Launch"
import config from "../config"

const TermsOfService = () => {
  const theme = useTheme()

  // Handle external navigation
  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  return (
    <Container maxWidth="md">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "rgba(13, 15, 25, 0.7)",
          border: "1px solid rgba(0, 229, 255, 0.1)",
          borderRadius: 2,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
          <GavelIcon
            sx={{
              fontSize: 40,
              color: theme.palette.primary.main,
              textShadow: "0 0 8px rgba(0, 229, 255, 0.6)",
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(45deg, #ffffff 30%, #00e5ff 90%)",
              backgroundClip: "text",
              textFillColor: "transparent",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 2px rgba(0, 229, 255, 0.5)",
            }}
          >
            Terms of Service
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using DarkEdge, you agree to be bound by these Terms of Service. If you disagree with any part
          of the terms, you may not access the service.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          2. User Responsibilities
        </Typography>
        <Typography variant="body1" paragraph>
          You are responsible for your use of the service and any content you provide, including compliance with
          applicable laws, rules, and regulations. You should use the service at your own risk and in accordance with
          the terms of service of the games you are playing.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          3. Subscriptions and Payments
        </Typography>
        <Typography variant="body1" paragraph>
          Some aspects of the service may be provided for a fee. You will be required to select a payment plan and
          provide accurate information regarding your payment method. You agree to pay all fees and charges incurred in
          connection with your account on the terms described in the payment plan.
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.1 Auto-Renewal
        </Typography>
        <Typography variant="body1" paragraph>
          Subscriptions will automatically renew until cancelled. By purchasing a subscription, you authorize us to
          charge your payment method for the subscription fee at the then-current rate, which may change from time to
          time. You can cancel your subscription at any time via the{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation("https://chat-tier.com/my/orders", "chat-tier Profile")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            chat-tier profile page
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>
          .
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.2 Cancellation Policy
        </Typography>
        <Typography variant="body1" paragraph>
          If you cancel your subscription, you will retain access until the end of your current billing period. No
          refunds are provided for partial months or unused subscription time. It is your responsibility to cancel your
          subscription before it renews if you do not want to be charged.
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.3 Discord Integration
        </Typography>
        <Typography variant="body1" paragraph>
          Subscriptions are implemented as Discord roles on our{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation(config.DISCORD_INVITE_LINK, "Discord Server")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Discord server
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>
          . When you purchase a subscription, you'll automatically receive the corresponding role, which grants you
          access to the bot and its features. If you lose your role (e.g., leave the server), you can reclaim it from{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation("https://chat-tier.com/my/orders", "chat-tier Profile")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            chat-tier
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>{" "}
          by clicking the gear icon in your profile and selecting "Reclaim".
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.4 Payment Processing
        </Typography>
        <Typography variant="body1" paragraph>
          We use{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation("https://chat-tier.com/my/orders", "chat-tier")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            chat-tier
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>{" "}
          as our official payment processor. When making a purchase, you will be redirected to their platform to
          complete the transaction. Your payment information is handled according to their privacy policy and terms of
          service. We do not store your payment information on our servers.
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.5 Billing Issues
        </Typography>
        <Typography variant="body1" paragraph>
          For any billing issues or questions about your subscription, please contact us through our{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation(config.DISCORD_INVITE_LINK, "Discord Server")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Discord server
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>{" "}
          for the fastest response or use our{" "}
          <Link
            component={RouterLink}
            to="/contact"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Contact page
          </Link>
          . We will make reasonable efforts to address billing concerns, but ultimate resolution may depend on our
          payment processor's policies.
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: "medium", color: theme.palette.primary.main, mt: 3, mb: 1 }}>
          4.6 Refund Policy
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>No Refunds:</strong> All purchases are final and non-refundable. We do not provide refunds for any
          subscription fees paid, regardless of the circumstances, including but not limited to: dissatisfaction with
          the service, accidental purchases, lack of usage, technical issues, or account termination (whether initiated
          by you or us). By making a purchase, you acknowledge and agree to this no-refund policy. We recommend trying
          the free features of our{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation(config.DISCORD_INVITE_LINK, "Discord Server")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Discord server
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>{" "}
          before purchasing a subscription.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          4. Intellectual Property
        </Typography>
        <Typography variant="body1" paragraph>
          The service and its original content, features, and functionality are and will remain the exclusive property
          of DarkEdge and its licensors. The service is protected by copyright, trademark, and other laws. Our
          trademarks and trade dress may not be used in connection with any product or service without the prior written
          consent.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          5. Termination
        </Typography>
        <Typography variant="body1" paragraph>
          We may terminate or suspend your account and bar access to the service immediately, without prior notice or
          liability, under our sole discretion, for any reason whatsoever and without limitation, including but not
          limited to a breach of the Terms.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          6. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          In no event shall DarkEdge, nor its directors, employees, partners, agents, suppliers, or affiliates, be
          liable for any indirect, incidental, special, consequential or punitive damages, including without limitation,
          loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or
          inability to access or use the service.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          7. Changes to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is
          material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a
          material change will be determined at our sole discretion.
        </Typography>

        <Typography variant="h6" sx={{ color: theme.palette.primary.main, mt: 4, mb: 2 }}>
          8. Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about these Terms, please contact us through our{" "}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleExternalNavigation(config.DISCORD_INVITE_LINK, "Discord Server")
            }}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Discord server
            <LaunchIcon sx={{ fontSize: "0.875rem" }} />
          </Link>{" "}
          or via our{" "}
          <Link
            component={RouterLink}
            to="/contact"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: "#00e5ff",
              },
            }}
          >
            Contact page
          </Link>
          .
        </Typography>
      </Paper>
    </Container>
  )
}

export default TermsOfService
